import React, { useMemo } from 'react';
import { Controller, get } from 'react-hook-form';
import PropTypes from 'prop-types';
import {
  MenuItem,
  ListItemText,
  Chip,
  Box,
  TextField,
  Typography
} from '@mui/material';

import useRemoveSpaces from '../utils/removeSpaces';

const EMPTY_ARRAY = [];

const FormMultipleSelect = ({
  control,
  formErrors,
  label,
  // Default to an empty array if options is undefined
  options = [],
  formFieldId,
  required,
  readonly,
  testId,
  validationOptions,
  ...rest
}) => {
  const testIdMemo = useRemoveSpaces(testId ?? `${ label }_Field`);

  // Ensure options is always an array
  const safeOptions = Array.isArray(options) ? options : EMPTY_ARRAY;

  // Prepare a list of locked options (selections that cannot be removed)
  const lockedOptions = useMemo(() => (
    safeOptions.reduce((list, option) => {
      if (option.locked) {
        list.push(option.id);
      }

      return list;
    }, [])
  ), [safeOptions])

  return (
    <Controller
      name={formFieldId}
      control={control}
      rules={{
        required: required ? `${ label } is required` : undefined,
        ...validationOptions
      }}
      render={({ field }) => (
        <TextField
          select
          sx={{ overflow: 'visible' }}
          variant={readonly ? 'filled' : 'outlined'}
          disabled={readonly}
          required={required}
          fullWidth
          label={label}
          error={!!get(formErrors, formFieldId)}
          data-testid={testIdMemo}
          inputProps={{ readOnly: readonly }}
          SelectProps={{
            multiple: true,
            value: field.value ? field.value.map((item) => item.id) : [],
            onChange: (event) => {
              const newValue = event.target.value;

              // Locked options cannot be removed, concat the new value list and the locked options
              // together and then use a Set to de-duplicate the entries in the array
              const value = [
                ...(new Set(lockedOptions.concat(newValue)))
              ];

              const selectedOptions = safeOptions.filter((option) => value.includes(option.id));
              field.onChange(selectedOptions);
            },
            renderValue: (selectedIds) => (
              <Box style={{ display: 'flex', flexWrap: 'wrap' }}>
                {selectedIds.map((id) => {
                  const selectedOption = safeOptions.find((option) => option.id === id);
                  return (
                    <Chip
                      key={id}
                      label={selectedOption?.fullName}
                      onDelete={selectedOption?.locked ? null : (() => {
                        const newValue = field.value.filter(
                          (item) => item.id !== id
                        );
                        field.onChange(newValue);
                      })}
                      onMouseDown={(event) => {
                        event.stopPropagation();
                      }}
                      style={{ margin: 1, fontSize: 14 }}
                    />
                  );
                })}
              </Box>
            )
          }}
          {...rest}
        >
          {safeOptions.length > 0 ? (
            safeOptions.map((option, index) => (
              <MenuItem
                key={option.id}
                value={option.id}
                data-testid={`${ testIdMemo }_${ index }`}
              >
                <ListItemText primary={option.fullName} />
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>
              <Typography variant='body2' color='textSecondary'>
                No options available
              </Typography>
            </MenuItem>
          )}
        </TextField>
      )}
    />
  );
};

FormMultipleSelect.propTypes = {
  control: PropTypes.object.isRequired,
  formErrors: PropTypes.object,
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])).isRequired,
  formFieldId: PropTypes.string.isRequired,
  required: PropTypes.bool,
  readonly: PropTypes.bool,
  testId: PropTypes.string,
  validationOptions: PropTypes.object
};

FormMultipleSelect.defaultProps = {
  formErrors: null,
  required: false,
  readonly: false,
  testId: null,
  validationOptions: {}
};

export default FormMultipleSelect;
